/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { useEffect, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { breakpoints, useDeviceInfo } from '@airasia-rn/ui';

/**
 * SSR-safe and resize-reactive hook for detecting small screen widths (< md breakpoint).
 *
 * During SSR and the first client render, returns false (desktop) to ensure
 * the client hydration matches the server-rendered HTML. After mount,
 * returns the actual value and updates on window resize.
 *
 * Replaces direct calls to `getCurrentBreakpoint() < 2` in components that
 * conditionally render different portal-backed sheets (BottomSheetView vs
 * RightSheetView), where a mismatch between SSR and client output causes a
 * React hydration crash.
 */

const useIsScreenSmallerThan = (breakpoint: number): boolean => {
  const [mounted, setMounted] = useState(false);
  const { width } = useWindowDimensions();
  // Until mounted, answer from the server's device hint (a phone is below
  // every breakpoint up to md) so the server render and the first client
  // render agree and a phone gets the small-screen layout on first paint,
  // instead of a flat `false` that re-laid out the page after mount.
  const { isMobileWidth } = useDeviceInfo();

  useEffect(() => {
    setMounted(true);
  }, []);

  if (mounted) return width < breakpoint;
  return breakpoint <= breakpoints.md ? isMobileWidth : false;
};

export const useIsSmallScreen = (): boolean =>
  useIsScreenSmallerThan(breakpoints.md);

/**
 * SSR-safe and resize-reactive hook for detecting extra-small screen widths (< sm breakpoint).
 */
export const useIsExtraSmallScreen = (): boolean =>
  useIsScreenSmallerThan(breakpoints.sm);

/**
 * Mobile web: the web build on a screen narrower than `md`. Always `false` on
 * iOS and Android. Use it to pick the web-only bottom sheet over Phoenix's.
 */
export const useIsMobileWeb = (): boolean => {
  const isSmallScreen = useIsSmallScreen();
  return Platform.OS === 'web' && isSmallScreen;
};
